import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMail, FiUser, FiSend, FiCheck } from 'react-icons/fi';
import { AiOutlineTwitter } from "react-icons/ai";
import { BsLinkedin, BsGithub } from "react-icons/bs";
import { FaMediumM } from "react-icons/fa";

const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const formData = new FormData();
      formData.append("access_key", WEB3FORMS_ACCESS_KEY);
      formData.append("from_name", "Portfolio Message");
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("message", form.message);
      // Optional subject for your inbox
      formData.append("subject", `${form.name} Message from your Portfolio!`);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setForm({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const isSuccess = status === 'success';
  const isSending = status === 'sending';

  return (
    <section id='contact' className='py-16 md:py-20 bg-[#040811] relative overflow-hidden'>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
        <div className="w-[800px] h-[800px] bg-gradient-to-r from-primary to-accent rounded-full filter blur-[120px] mix-blend-screen opacity-30" />
      </div>

      <div className='max-w-2xl mx-auto px-6 lg:px-8 relative z-10'>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-slate-800/20 border border-slate-700/50 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl"
        >
          {/* Header */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className='text-4xl md:text-5xl font-bold text-white mb-4'>
              Let's build something{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                extraordinary.
              </span>
            </h2>
            <p className='text-slate-400 text-lg leading-relaxed'>
              Have a project in mind or just want to say hi? Send me a message, I'll get back to you soon.
            </p>
          </motion.div>

          {/* Success state */}
          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-10 space-y-4 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <FiCheck className="text-green-400 text-3xl" />
              </div>
              <h3 className="text-white text-xl font-semibold">Message sent!</h3>
              <p className="text-slate-400">Thanks for reaching out. I'll reply as soon as I can.</p>
              <button
                onClick={() => setStatus('idle')}
                className="text-primary text-sm underline underline-offset-4 mt-2"
              >
                Send another message
              </button>
            </motion.div>
          ) : (
            // Form
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div className="relative">
                <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              {/* Email */}
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-11 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              {/* Message */}
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Message here.."
                required
                rows={5}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary transition-colors resize-none"
              />

              {/* Error */}
              {status === 'error' && (
                <p className="text-red-400 text-sm text-center">
                  Something went wrong. Please try again or email me directly at{' '}
                  <a href="mailto:etim.utibe@gmail.com" className="underline">
                    etim.utibe@gmail.com
                  </a>
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isSending}
                className="w-full inline-flex items-center justify-center space-x-2 px-8 py-4 bg-primary text-white font-medium text-lg rounded-xl hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-95 shadow-[0_0_20px_rgba(79,70,229,0.4)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSending ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <FiSend />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* Direct Social Connect Section */}
          <div className="mt-8 pt-6 border-t border-slate-700/60">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Or reach out directly:
                </p>
                <a
                  href="mailto:etim.utibe@gmail.com"
                  className="text-sm font-medium text-secondary hover:text-cyan-300 transition-colors inline-flex items-center gap-1.5 mt-1"
                >
                  <FiMail className="text-base" />
                  <span>etim.utibe@gmail.com</span>
                </a>
              </div>

              <div className="flex items-center space-x-3">
                <a
                  href="https://github.com/Uticodes"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="GitHub Profile"
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-secondary border border-slate-700/70 hover:border-secondary/50 transition-all duration-200 hover:scale-110 active:scale-95 shadow-sm"
                  title="GitHub"
                >
                  <BsGithub size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/in/utibe-etim-0a901a107/"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn Profile"
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-secondary border border-slate-700/70 hover:border-secondary/50 transition-all duration-200 hover:scale-110 active:scale-95 shadow-sm"
                  title="LinkedIn"
                >
                  <BsLinkedin size={18} />
                </a>
                <a
                  href="https://twitter.com/Uticodes"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Twitter Profile"
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-secondary border border-slate-700/70 hover:border-secondary/50 transition-all duration-200 hover:scale-110 active:scale-95 shadow-sm"
                  title="Twitter"
                >
                  <AiOutlineTwitter size={20} />
                </a>
                <a
                  href="https://medium.com/@utibeetim"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Medium Profile"
                  className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-secondary border border-slate-700/70 hover:border-secondary/50 transition-all duration-200 hover:scale-110 active:scale-95 shadow-sm"
                  title="Medium"
                >
                  <FaMediumM size={18} />
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
